import { Button, Checkbox, Spinner, UIText2 } from '@cian/ui-kit';

import { clone } from 'ramda';
import { type ChangeEvent, useCallback, useEffect, useMemo } from 'react';

import type { TModifier } from '@/packages/Filters';
import type { NonEmptyArray, TJsonQuery } from '@/packages/JsonQuery';
import type { IJsonQueryHighway } from '@/packages/api-models/common/json_query';
import { ASYNC_STATUS } from '@/packages/typesafe-result';

import type { IHighway } from '../../../../types/highways';
import { HighwaysModalContent } from '../components/HighwaysModalContent';
import { useCurrentLocationHighways } from '../hooks/useCurrentLocationHighways';

export function HighwaysModalContentContainer({
  onChange,
  jsonQuery,
}: {
  onChange: (...modifiers: NonEmptyArray<TModifier>) => void;
  jsonQuery: TJsonQuery;
}): JSX.Element {
  const { status, data, request } = useCurrentLocationHighways();

  const selectedHighwayIds = useMemo(() => {
    const geo = (jsonQuery.geo && clone(jsonQuery.geo.value)) || [];

    return geo.filter(item => item.type === 'highway').map((item: IJsonQueryHighway) => item.id);
  }, [jsonQuery]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, selected: boolean): void => {
      const newSelected = selected
        ? [...selectedHighwayIds, parseInt(e.target.value, 10)]
        : selectedHighwayIds.filter(id => id !== parseInt(e.target.value, 10));
      onChange({
        action: 'setHighways',
        arguments: newSelected.map(id => ({ id, type: 'highway' })) as NonEmptyArray<IJsonQueryHighway>,
      });
    },
    [onChange, selectedHighwayIds],
  );

  const handleSelectGroup = useCallback(
    (highways: IHighway[]): (() => void) =>
      () => {
        const highwayIds = highways.map(highway => highway.id);

        if (highwayIds.some(highway => !selectedHighwayIds.includes(highway))) {
          onChange({
            action: 'setHighways',
            arguments: selectedHighwayIds
              .concat(highwayIds.filter(highway => !selectedHighwayIds.includes(highway)))
              .map(id => ({ id, type: 'highway' })) as NonEmptyArray<IJsonQueryHighway>,
          });
        } else {
          onChange({
            action: 'setHighways',
            arguments: selectedHighwayIds
              .filter(id => !highwayIds.includes(id))
              .map(id => ({ id, type: 'highway' })) as NonEmptyArray<IJsonQueryHighway>,
          });
        }
      },
    [onChange, selectedHighwayIds],
  );

  useEffect(() => {
    request();
  }, [request]);

  if (status === ASYNC_STATUS.PENDING) {
    return (
      <HighwaysModalContent.Centered>
        <Spinner />
      </HighwaysModalContent.Centered>
    );
  }

  if (status === ASYNC_STATUS.ERROR) {
    return (
      <HighwaysModalContent.Centered>
        <UIText2>
          Во время загрузки произошла ошибка. Пожалуйста, проверьте своё интернет соединение и попробуйте ещё раз.
        </UIText2>
        <Button size="XS" onClick={request}>
          Повторить
        </Button>
      </HighwaysModalContent.Centered>
    );
  }

  return (
    <HighwaysModalContent>
      {data.map((column, index) => (
        <HighwaysModalContent.Column key={`highway-column${index}`}>
          {column.map(highwayDirection => (
            <HighwaysModalContent.Group key={`${highwayDirection.name}`}>
              <HighwaysModalContent.Header
                title={highwayDirection.name}
                onClick={handleSelectGroup(highwayDirection.highways)}
              />
              <HighwaysModalContent.List>
                {highwayDirection.highways.map((highway: IHighway) => (
                  <HighwaysModalContent.ListItem key={`${highway.name}${index}`}>
                    <Checkbox
                      checked={selectedHighwayIds.includes(highway.id)}
                      label={highway.fullName}
                      value={`${highway.id}`}
                      onChange={handleChange}
                    />
                  </HighwaysModalContent.ListItem>
                ))}
              </HighwaysModalContent.List>
            </HighwaysModalContent.Group>
          ))}
        </HighwaysModalContent.Column>
      ))}
    </HighwaysModalContent>
  );
}
